import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { PROJECTS } from "../data/content";
import FlipBook from "../components/FlipBook";

function renderContent(text) {
  return text.split("\n\n").map((block, i) => {
    if (block.startsWith("## ")) {
      return (
        <h2 key={i} className="font-mono font-bold text-xl text-gray-800 mt-8 mb-3">
          {block.replace("## ", "")}
        </h2>
      );
    }
    if (block.startsWith("> ")) {
      return (
        <blockquote key={i} className="border-l-4 border-mint-light bg-mint-pastel/40 rounded-r-2xl px-5 py-3 my-4 font-sans text-gray-600 text-sm italic">
          {block.replace("> ", "")}
        </blockquote>
      );
    }
    const parts = block.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\))/g).map((part, j) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={j}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith("*") && part.endsWith("*")) {
        return <em key={j}>{part.slice(1, -1)}</em>;
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        return <a key={j} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="text-sky-600 underline hover:text-sky-800">{linkMatch[1]}</a>;
      }
      return part;
    });
    return (
      <p key={i} className="font-sans text-gray-700 text-base leading-relaxed mb-5">
        {parts}
      </p>
    );
  });
}

export default function ProjectPost() {
  const { id } = useParams();
  const project = PROJECTS.find((p) => p.id === Number(id));

  if (!project) return <Navigate to="/projects" replace />;

  const currentIndex = PROJECTS.findIndex((p) => p.id === project.id);
  const prev = PROJECTS[currentIndex - 1] || null;
  const next = PROJECTS[currentIndex + 1] || null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">

      <Link to="/projects" className="inline-flex items-center gap-2 font-mono text-sm text-gray-500 hover:text-gray-800 transition-colors mb-8">
        <ArrowLeft size={16} /> Quay lại Dự án
      </Link>

      {!project.thumbnail && (
        <div className={`w-full h-52 rounded-3xl bg-gradient-to-br ${project.bg} flex items-center justify-center text-7xl mb-8`}>
          {project.emoji}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        <span className="tag">{project.status}</span>
        {project.tags.map((t) => (
          <span key={t} className="tag">{t}</span>
        ))}
      </div>

      <h1 className="font-sans font-bold text-2xl md:text-3xl text-gray-900 leading-snug mb-4">
        {project.title}
      </h1>

      <p className="font-sans text-gray-700 text-base leading-relaxed mb-8 pb-8 border-b border-gray-400">
        {project.description}
      </p>

      {project.thumbnail && (
        <div className="w-full flex justify-center mb-8">
          <img src={project.thumbnail} alt={project.title} className="max-h-[480px] w-auto shadow-md" />
        </div>
      )}

      {project.pdfUrl && (
        <div className="mb-10">
          <h2 className="font-sans font-bold text-lg text-gray-800 mb-4 flex items-center gap-2"><FileText size={20} /> Xem tài liệu</h2>
          <FlipBook pdfUrl={project.pdfUrl} />
        </div>
      )}

      <div>
        {renderContent(project.content)}
      </div>

      <div className="mt-12 grid grid-cols-2 gap-4">
        {prev && (
          <Link to={`/projects/${prev.id}`} className="card hover:-translate-y-1 transition-all duration-300 col-start-1 p-5">
            <p className="font-sans text-xs text-gray-400 mb-2">← Dự án trước</p>
            <p className="font-sans font-semibold text-sm text-gray-700 line-clamp-2 leading-relaxed">{prev.title}</p>
          </Link>
        )}
        {next && (
          <Link to={`/projects/${next.id}`} className="card hover:-translate-y-1 transition-all duration-300 col-start-2 text-right p-5">
            <p className="font-sans text-xs text-gray-400 mb-2">Dự án sau →</p>
            <p className="font-sans font-semibold text-sm text-gray-700 line-clamp-2 leading-relaxed">{next.title}</p>
          </Link>
        )}
      </div>

    </div>
  );
}
